"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMounted } from "@/lib/hooks/use-mounted";

function LoginContent() {
  const searchParams = useSearchParams();
  const next = searchParams.get("next");
  const reset = searchParams.get("reset");
  const safeNext = next?.startsWith("/") ? next : "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const ready = useMounted();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Erreur de connexion");
        return;
      }
      window.location.replace(safeNext);
    } catch {
      setError("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center space-y-2">
          <Image src="/logo.svg" alt="OpusLouange" width={40} height={40} className="w-10 h-10 mx-auto" />
          <h1 className="text-2xl font-semibold tracking-tight">Connexion</h1>
          <p className="text-muted-foreground text-sm">Accédez à votre espace OpusLouange</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {reset && (
            <p className="text-sm bg-muted px-3 py-2 rounded-lg">
              Mot de passe mis à jour. Vous pouvez vous connecter.
            </p>
          )}
          {error && (
            <p data-testid="auth-error" className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="vous@exemple.com"
              className="h-10"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Mot de passe</Label>
              <Link href="/auth/forgot-password" className="text-xs text-muted-foreground hover:text-foreground hover:underline underline-offset-4">
                Mot de passe oublié ?
              </Link>
            </div>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              className="h-10"
            />
          </div>
          <Button data-testid="login-submit" type="submit" disabled={!ready || loading} className="w-full h-10 text-sm font-medium">
            {loading ? "Connexion…" : "Se connecter"}
          </Button>
        </form>

        <p className="text-center text-sm text-muted-foreground">
          Pas encore de compte ?{" "}
          <Link href={next ? `/auth/signup?next=${encodeURIComponent(next)}` : "/auth/signup"} className="text-foreground font-medium hover:underline underline-offset-4">
            Créer un compte
          </Link>
        </p>
      </div>
    </div>
  );
}

export function LoginForm() {
  return (
    <Suspense>
      <LoginContent />
    </Suspense>
  );
}
